// /admin → Analytics — the numbers worth showing somebody, in one place.
//
// Every other tab on /admin is a working surface: a queue, a log, a table an
// operator reads to decide what to fix. This one is the opposite — it is what
// gets put on a projector in front of university admin, or sent to a café owner
// asking whether to advertise — so it leads with pictures and a date range, and
// ends with a download that can hand over exactly the slice asked for.
//
// ── Where each number comes from ────────────────────────────────────────────
// ⚠️ Every chart says which kind of source it is drawn from, because the three
// kinds answer a date range differently and the difference is invisible once
// it is a picture:
//   · per-day rollups (route searches, Nearby, events, app installs,
//     restaurants) — kept forever, so the range is EXACT;
//   · row samples (route ratings) — a capped rolling list, so the range filters
//     what is still held;
//   · all-time tallies (most-looked-at places) — no dates at all, so the range
//     does not apply, and the card says so rather than pretending.
//
// The restaurant stats are fetched here, separately, on first open — see
// `?part=restaurants` in api/admin-data.js for why they are not in the main
// payload. The arithmetic is in utils/analytics.js, which is tested.

import { useEffect, useMemo, useRef, useState } from 'react';
import { ChartCard, TrendChart, DonutChart, BarList, ColumnChart } from './Charts.jsx';
import { downloadCsv } from '../utils/adminExport.js';
import { downloadXlsx } from '../utils/xlsx.js';
import { KIND_LABELS, NEARBY_KINDS } from '../utils/adminLabels.js';
import {
  RANGE_PRESETS, rangeFromPreset, rangeLabel, rangeSlug, dayOf,
  daysIn, sumField, seriesOf, weeklyOf, weekLabel, tsInRange,
  restaurantBreakdown, restaurantItems, restaurantQueries, restaurantDays, restaurantReport,
  DATASETS, datasetFor, tableToObjects, RESTAURANT_EVENTS,
} from '../utils/analytics.js';

const nf = (n) => Number(n || 0).toLocaleString('en-CA');
const pctOf = (a, b) => (b ? `${Math.round((a / b) * 100)}%` : '—');

function downloadJson(filename, value) {
  const blob = new Blob([JSON.stringify(value, null, 2)], { type: 'application/json' });
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = filename;
  a.click();
  setTimeout(() => URL.revokeObjectURL(url), 1000);
}

const safeFile = (s) => String(s || '').toLowerCase().replace(/[^a-z0-9]+/g, '-').replace(/^-|-$/g, '').slice(0, 40) || 'restaurant';

export default function AnalyticsTab({ data, token, apiBase, fetchAllRoutes, onExpired }) {
  const [preset, setPreset] = useState('30');
  const [custom, setCustom] = useState({ from: '', to: '' });
  const [restaurants, setRestaurants] = useState(null);   // { daily, names }
  const [restErr, setRestErr] = useState('');
  const [pickRestaurant, setPickRestaurant] = useState('');
  // The download panel keeps its OWN choice of range, defaulting to the page's.
  // "Show me September, send me the whole year" is a normal thing to want.
  const [dlKey, setDlKey] = useState(DATASETS[0].key);
  const [dlRangeMode, setDlRangeMode] = useState('page'); // page | all
  const [dlRestaurant, setDlRestaurant] = useState('');
  const [dlBusy, setDlBusy] = useState('');

  // Through a ref: the host passes an inline arrow, and keying the fetch on it
  // would refetch the restaurant stats on every render of the dashboard.
  const expiredRef = useRef(onExpired);
  expiredRef.current = onExpired;

  useEffect(() => {
    let alive = true;
    (async () => {
      try {
        const r = await fetch(`${apiBase}/api/admin-data?part=restaurants`, {
          headers: { Authorization: `Bearer ${token}` },
        });
        if (r.status === 401) { expiredRef.current?.(); return; }
        const j = await r.json().catch(() => ({}));
        if (!alive) return;
        if (!r.ok) { setRestErr(j.error || 'Could not load restaurant analytics.'); return; }
        setRestaurants({ daily: Array.isArray(j.daily) ? j.daily : [], names: j.names || { restaurants: {}, items: {} } });
      } catch {
        if (alive) setRestErr('Could not load restaurant analytics.');
      }
    })();
    return () => { alive = false; };
  }, [apiBase, token]);

  const today = dayOf(new Date());
  const range = useMemo(() => rangeFromPreset(preset, today, custom), [preset, today, custom]);

  // ── Campus map ──
  const map = useMemo(() => {
    const days = daysIn(data?.daily, range);
    const s = (f) => sumField(days, f);
    const searches = seriesOf(data?.daily, 'searches', range);
    return {
      searches: s('searches'), sessions: s('sessions'), found: s('found'),
      app: s('ios') + s('android'),
      series: searches,
      weekly: weeklyOf(searches).map((w) => ({
        label: weekLabel(w.week).split(' – ')[0], value: w.value,
        title: `${weekLabel(w.week, { long: true })}: ${nf(w.value)} searches`,
      })),
      devices: [
        { label: 'Desktop browser', value: s('desktop') },
        { label: 'Phone browser', value: s('mobile') },
        { label: 'iPhone app', value: s('ios') },
        { label: 'Android app', value: s('android') },
      ],
      options: [
        { label: 'Accessible routes', value: s('opt:accessible') },
        { label: 'Prefer tunnels', value: s('opt:tunnels') },
        { label: 'Stairs only', value: s('opt:noElevators') },
        { label: 'Found by course code', value: s('opt:course') },
      ],
      returning: s('returning'),
    };
  }, [data, range]);

  const nearby = useMemo(() => {
    const days = daysIn(data?.nearby?.daily, range);
    return {
      views: sumField(days, 'ev:view'),
      gos: sumField(days, 'ev:go'),
      kinds: NEARBY_KINDS.map((k) => ({ label: KIND_LABELS[k] || k, value: sumField(days, `kind:${k}`) })),
      // ALL TIME — the per-place tally has no dates. Said on the card.
      places: [...(data?.nearby?.places || [])]
        .sort((a, b) => (b.views || 0) - (a.views || 0))
        .map((p) => ({ label: p.name || p.placeId, sub: KIND_LABELS[p.kind] || p.kind || '', value: p.views || 0 })),
    };
  }, [data, range]);

  const events = useMemo(() => {
    const days = daysIn(data?.eventUse?.daily, range);
    return {
      opens: sumField(days, 'opens'),
      pins: sumField(days, 'ev:pin'),
      rows: sumField(days, 'ev:row'),
      directions: sumField(days, 'ev:directions'),
      series: seriesOf(data?.eventUse?.daily, 'opens', range),
    };
  }, [data, range]);

  const app = useMemo(() => {
    const days = daysIn(data?.appProfileDaily, range);
    const ratings = (data?.routeRatings || []).filter((r) => tsInRange(r.ts, range));
    const count = (v) => ratings.filter((r) => r.verdict === v).length;
    return {
      installs: sumField(days, 'installs'),
      ios: sumField(days, 'ios'),
      android: sumField(days, 'android'),
      series: seriesOf(data?.appProfileDaily, 'installs', range),
      ratings: ratings.length,
      verdicts: [
        { label: 'Good', value: count('good') },
        { label: 'Ok', value: count('ok') },
        { label: 'Bad', value: count('bad') },
      ],
    };
  }, [data, range]);

  const food = useMemo(() => {
    if (!restaurants) return null;
    const breakdown = restaurantBreakdown(restaurants.daily, restaurants.names, range);
    const days = restaurantDays(restaurants.daily, range);
    const tot = (k) => days.reduce((n, d) => n + (d[k] || 0), 0);
    return {
      breakdown,
      views: tot('restaurant_view'),
      directions: tot('directions'),
      dishSearches: tot('dish_search'),
      menuSearches: tot('menu_search'),
      series: days.map((d) => ({ day: d.day, value: d.restaurant_view })),
      items: restaurantItems(restaurants.daily, restaurants.names, range)
        .map((x) => ({ label: x.item, sub: x.restaurant, value: x.views })),
      queries: restaurantQueries(restaurants.daily, restaurants.names, range)
        .map((x) => ({ label: x.query, sub: x.where, value: x.count })),
    };
  }, [restaurants, range]);

  const restaurantOptions = food?.breakdown || [];

  // ── Downloads ──
  const dataset = datasetFor(dlKey);
  const dlRange = useMemo(() => (dlRangeMode === 'all' ? { from: null, to: null } : range), [dlRangeMode, range]);

  const runDownload = async (format) => {
    if (!dataset) return;
    setDlBusy(format);
    try {
      let full = data;
      if (dataset.needsAllRoutes && typeof fetchAllRoutes === 'function') {
        try { full = (await fetchAllRoutes(data)) || data; } catch { /* the screen's copy, then */ }
      }
      const opts = { restaurantId: dlRestaurant };
      const table = dataset.build({ data: full, restaurants }, dlRange, opts);
      const who = dataset.needsRestaurant
        ? `-${safeFile(restaurantOptions.find((r) => r.id === dlRestaurant)?.name)}` : '';
      const base = `umaps-${dataset.key}${who}-${rangeSlug(dlRange)}`;
      if (format === 'csv') downloadCsv(`${base}.csv`, table.header, table.rows);
      else {
        downloadJson(`${base}.json`, {
          dataset: dataset.label,
          period: rangeLabel(dlRange),
          source: SPAN_WORDS[dataset.span],
          generated: new Date().toISOString(),
          rows: tableToObjects(table),
        });
      }
    } finally { setDlBusy(''); }
  };

  const dlPreviewRows = useMemo(() => {
    if (!dataset) return 0;
    if (dataset.key.startsWith('restaurant') && !restaurants) return 0;
    try {
      return dataset.build({ data, restaurants }, dlRange, { restaurantId: dlRestaurant }).rows.length;
    } catch { return 0; }
  }, [dataset, data, restaurants, dlRange, dlRestaurant]);

  const downloadReport = (rid) => {
    const rep = restaurantReport(restaurants, range, rid);
    if (!rep) return;
    downloadXlsx(`umaps-${safeFile(rep.name)}-${rangeSlug(range)}.xlsx`, rep.sheets);
  };

  const period = rangeLabel(range);
  const dlDisabled = !!dlBusy
    || (!dlPreviewRows && !dataset?.needsAllRoutes)
    || (!!dataset?.needsRestaurant && !dlRestaurant);

  return (
    <div>
      {/* ── Range ── */}
      <div style={S.rangeBar}>
        <div style={S.rangeTitle}>
          <div style={S.rangeHeading}>UMaps at a glance</div>
          <div style={S.rangePeriod}>{period}</div>
        </div>
        <div style={S.rangeControls}>
          {RANGE_PRESETS.map((p) => (
            <button key={p.id} style={{ ...S.chip, ...(preset === p.id ? S.chipOn : {}) }}
                    onClick={() => setPreset(p.id)}>{p.label}</button>
          ))}
          {preset === 'custom' && (
            <span style={S.customRange}>
              <input type="date" style={S.dateInput} value={custom.from} max={custom.to || today}
                     onChange={(e) => setCustom((c) => ({ ...c, from: e.target.value }))} aria-label="From" />
              <span style={S.dim}>to</span>
              <input type="date" style={S.dateInput} value={custom.to} min={custom.from || undefined} max={today}
                     onChange={(e) => setCustom((c) => ({ ...c, to: e.target.value }))} aria-label="To" />
            </span>
          )}
          {/* The browser's own print-to-PDF: a one-page handout for a meeting
              with no export pipeline behind it. */}
          <button style={S.btnGhost} onClick={() => window.print()}>Print / PDF</button>
        </div>
      </div>

      {/* ── Headline numbers ── */}
      <div style={S.kpis}>
        <Kpi label="Route searches" value={nf(map.searches)} hint={`${nf(map.sessions)} distinct visits`} />
        <Kpi label="Found a route" value={pctOf(map.found, map.searches)} hint={`${nf(map.found)} of ${nf(map.searches)}`} />
        <Kpi label="Searched in the app" value={pctOf(map.app, map.searches)} hint={`${nf(map.app)} from iPhone and Android`} />
        <Kpi label="New app installs" value={nf(app.installs)} hint={`${nf(app.ios)} iPhone · ${nf(app.android)} Android`} />
        <Kpi label="Places opened" value={nf(nearby.views)} hint={`${nf(nearby.gos)} asked for directions`} />
        <Kpi label="Events opened" value={nf(events.opens)} hint={`${nf(events.directions)} asked for directions`} />
        <Kpi label="Restaurant page views" value={food ? nf(food.views) : '…'}
             hint={food ? `${nf(food.directions)} asked for directions` : 'loading'} />
      </div>

      {/* ── Campus map ── */}
      <Section title="Finding rooms" note="Every settled From → To search on the website and in the app.">
        <div style={S.grid}>
          <div style={S.wide}>
            <ChartCard title="Route searches per day" hint="Per-day counters, kept forever — exact for any range."
                       empty={map.series.some((p) => p.value) ? null : 'No searches in this period.'}>
              <TrendChart points={map.series} label="Route searches per day" />
            </ChartCard>
          </div>
          <div style={S.wide}>
            <ChartCard title="Route searches per week" hint="Sunday to Saturday. Hover a bar for the dates."
                       empty={map.weekly.some((w) => w.value) ? null : 'No searches in this period.'}>
              <ColumnChart bars={map.weekly} label="Route searches per week" />
            </ChartCard>
          </div>
          <ChartCard title="Where people search from" empty={map.searches ? null : 'No searches in this period.'}>
            <DonutChart slices={map.devices} unit="searches" />
          </ChartCard>
          <ChartCard title="Route options used"
                     hint="Searches that turned each option on. One search can use several."
                     empty={map.searches ? null : 'No searches in this period.'}>
            <BarList items={map.options} unit="searches" emptyText="No options used in this period." />
          </ChartCard>
        </div>
      </Section>

      {/* ── Nearby ── */}
      <Section title="Places on campus" note="The Nearby panel: study spots, libraries, washrooms, bus stops, microwaves and restaurants.">
        <div style={S.grid}>
          <ChartCard title="What people look for" hint="Place opens by kind, in this period."
                     empty={nearby.views ? null : 'Nothing opened in this period.'}>
            <DonutChart slices={nearby.kinds} unit="opens" />
          </ChartCard>
          <ChartCard title="Most-looked-at places"
                     hint="ALL TIME — this tally has no dates, so the range above does not apply to it."
                     empty={nearby.places.length ? null : 'No places opened yet.'}>
            <BarList items={nearby.places} max={10} unit="opens" />
          </ChartCard>
        </div>
      </Section>

      {/* ── Events ── */}
      <Section title="Club events" note="Opened from a map pin or the events calendar, on the website or in the app.">
        <div style={S.grid}>
          <div style={S.wide}>
            <ChartCard title="Events opened per day" empty={events.series.some((p) => p.value) ? null : 'No events opened in this period.'}>
              <TrendChart points={events.series} label="Events opened per day" />
            </ChartCard>
          </div>
          <ChartCard title="How people find an event" empty={events.opens ? null : 'No events opened in this period.'}>
            <DonutChart unit="opens" slices={[
              { label: 'Map pin', value: events.pins },
              { label: 'Events calendar', value: events.rows },
            ]} />
          </ChartCard>
          <ChartCard title="From reading to going"
                     hint="Of the events opened, how many people then asked to be walked there."
                     empty={events.opens ? null : 'No events opened in this period.'}>
            <BarList unit="" items={[
              { label: 'Opened an event', value: events.opens },
              { label: 'Asked for directions', value: events.directions },
            ]} />
          </ChartCard>
        </div>
      </Section>

      {/* ── Phone app ── */}
      <Section title="The phone app" note="Installs are distinct phones answering the first-run questions; ratings are the thumbs after a route.">
        <div style={S.grid}>
          <div style={S.wide}>
            <ChartCard title="App installs, running total" hint="Within the selected period."
                       empty={app.series.some((p) => p.value) ? null : 'No installs in this period.'}>
              <TrendChart points={app.series} cumulative label="App installs, running total" />
            </ChartCard>
          </div>
          <ChartCard title="How routes were rated"
                     hint="From the rolling ratings sample — the range filters what is still held."
                     empty={app.ratings ? null : 'No ratings in this period.'}>
            <DonutChart slices={app.verdicts} unit="ratings" />
          </ChartCard>
        </div>
      </Section>

      {/* ── Restaurants ── */}
      <Section title="Restaurants"
               note="What students do with campus food in the app: which counters they open, which dishes, what they search for, and whether they go.">
        {restErr && <div style={S.err}>{restErr}</div>}
        {!food && !restErr && <div style={S.dim}>Loading restaurant analytics…</div>}
        {food && (
          <>
            <div style={S.grid}>
              <div style={S.wide}>
                <ChartCard title="Restaurant page views per day"
                           empty={food.series.some((p) => p.value) ? null : 'No restaurant pages opened in this period.'}>
                  <TrendChart points={food.series} label="Restaurant page views per day" />
                </ChartCard>
              </div>
              <ChartCard title="Views per restaurant" empty={food.views ? null : 'No restaurant pages opened in this period.'}>
                <BarList items={food.breakdown.map((r) => ({ label: r.name, value: r.restaurant_view }))} unit="views" />
              </ChartCard>
              <ChartCard title="Top dishes" hint="Menu items opened." empty={food.items.length ? null : 'No dishes opened in this period.'}>
                <BarList items={food.items} unit="opens" />
              </ChartCard>
              <div style={S.wide}>
                <ChartCard title="What people searched for"
                           hint={`${nf(food.dishSearches)} searches across every menu, ${nf(food.menuSearches)} inside one restaurant's menu. Lower-cased, so "Pho" and "pho" are one row.`}
                           empty={food.queries.length ? null : 'No food searches in this period.'}>
                  <BarList items={food.queries} max={15} unit="searches" />
                </ChartCard>
              </div>
            </div>

            {/* The pitch table: one row per business, and a report per row that
                carries that business's numbers and nobody else's. */}
            <div style={S.sectionTitle}>Per-restaurant breakdown · {period}</div>
            <div style={S.tableWrap}>
              <table style={S.table}>
                <thead><tr>
                  <th style={S.th}>Restaurant</th>
                  <th style={S.th} title="Distinct visitors per day, summed over the period">Visitors</th>
                  {RESTAURANT_EVENTS.map(([k, label]) => <th key={k} style={S.th}>{label}</th>)}
                  <th style={S.th} title="Directions per page view">Went</th>
                  <th style={S.th}>Report</th>
                </tr></thead>
                <tbody>
                  {food.breakdown.map((r, i) => (
                    <tr key={r.id} style={i % 2 ? S.trAlt : undefined}>
                      <td style={S.td}>{r.name}</td>
                      <td style={S.td}>{nf(r.visitors)}</td>
                      {RESTAURANT_EVENTS.map(([k]) => <td key={k} style={S.td}>{nf(r[k])}</td>)}
                      <td style={S.td}>{r.directionsPct == null ? '—' : `${r.directionsPct}%`}</td>
                      <td style={S.td}>
                        <button style={S.linkBtn} onClick={() => downloadReport(r.id)}
                                title="Summary, day by day, dishes and menu searches — this restaurant only">
                          .xlsx
                        </button>
                      </td>
                    </tr>
                  ))}
                  {food.breakdown.length === 0 && (
                    <tr><td style={S.td} colSpan={RESTAURANT_EVENTS.length + 4}>
                      Nothing recorded yet — rows appear once the app starts sending restaurant activity.
                    </td></tr>
                  )}
                </tbody>
              </table>
            </div>
            {restaurantOptions.length > 0 && (
              <div style={S.pitchRow}>
                <select style={S.select} value={pickRestaurant} onChange={(e) => setPickRestaurant(e.target.value)}>
                  <option value="">Pick a restaurant for its report…</option>
                  {restaurantOptions.map((r) => <option key={r.id} value={r.id}>{r.name}</option>)}
                </select>
                <button style={S.btnGhost} disabled={!pickRestaurant} onClick={() => downloadReport(pickRestaurant)}>
                  Download its report ({period})
                </button>
                <span style={S.dim}>Its own figures only — never another business's, and never the campus-wide searches.</span>
              </div>
            )}
          </>
        )}
      </Section>

      {/* ── Download ── */}
      <Section title="Download data" note="Pick one table and a period. CSV opens in Excel or Sheets; JSON is for anything else.">
        <div style={S.dlCard}>
          <label style={S.dlField}>
            <span style={S.dlLabel}>Dataset</span>
            <select style={S.select} value={dlKey} onChange={(e) => setDlKey(e.target.value)}>
              {['Campus map', 'Phone app', 'Restaurants'].map((g) => (
                <optgroup key={g} label={g}>
                  {DATASETS.filter((d) => d.group === g).map((d) => <option key={d.key} value={d.key}>{d.label}</option>)}
                </optgroup>
              ))}
            </select>
          </label>
          {dataset?.needsRestaurant && (
            <label style={S.dlField}>
              <span style={S.dlLabel}>Restaurant</span>
              <select style={S.select} value={dlRestaurant} onChange={(e) => setDlRestaurant(e.target.value)}>
                <option value="">Choose…</option>
                {restaurantOptions.map((r) => <option key={r.id} value={r.id}>{r.name}</option>)}
              </select>
            </label>
          )}
          <label style={S.dlField}>
            <span style={S.dlLabel}>Period</span>
            <select style={S.select} value={dlRangeMode} onChange={(e) => setDlRangeMode(e.target.value)}
                    disabled={dataset?.span === 'total'}>
              <option value="page">{period} (as above)</option>
              <option value="all">All time</option>
            </select>
          </label>
          <div style={S.dlButtons}>
            <button style={S.btnPrimary} disabled={dlDisabled}
                    onClick={() => runDownload('csv')}>{dlBusy === 'csv' ? 'Collecting…' : 'Download CSV'}</button>
            <button style={S.btnGhost} disabled={dlDisabled}
                    onClick={() => runDownload('json')}>{dlBusy === 'json' ? 'Collecting…' : 'JSON'}</button>
          </div>
          <div style={S.dlNote}>
            {dataset && SPAN_WORDS[dataset.span]}
            {dataset?.needsAllRoutes
              ? ' The whole route log is fetched when you press it, so the file can hold more rows than this screen.'
              : ` ${nf(dlPreviewRows)} row${dlPreviewRows === 1 ? '' : 's'}.`}
          </div>
        </div>
      </Section>
    </div>
  );
}

const SPAN_WORDS = {
  daily: 'From per-day counters that never expire, so the period is exact.',
  rows: 'From a rolling sample of recent rows, so the period filters what is still held.',
  total: 'An all-time tally with no dates in it — the period does not apply.',
};

function Kpi({ label, value, hint }) {
  return (
    <div style={S.kpi}>
      <div style={S.kpiValue}>{value}</div>
      <div style={S.kpiLabel}>{label}</div>
      {hint && <div style={S.kpiHint}>{hint}</div>}
    </div>
  );
}

function Section({ title, note, children }) {
  return (
    <section style={S.section}>
      <div style={S.sectionHead}>{title}</div>
      {note && <div style={S.sectionNote}>{note}</div>}
      {children}
    </section>
  );
}

const S = {
  rangeBar: {
    display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 12, flexWrap: 'wrap',
    background: '#fff', borderRadius: 12, padding: '12px 14px', boxShadow: '0 2px 12px rgba(0,0,0,0.08)', marginBottom: 12,
  },
  rangeTitle: { minWidth: 0 },
  rangeHeading: { fontSize: 17, fontWeight: 800 },
  rangePeriod: { fontSize: 13, color: '#5f6368', marginTop: 2 },
  rangeControls: { display: 'flex', gap: 6, flexWrap: 'wrap', alignItems: 'center' },
  customRange: { display: 'inline-flex', gap: 6, alignItems: 'center' },
  dateInput: { border: '1px solid #dadce0', borderRadius: 8, padding: '4px 8px', font: 'inherit', fontSize: 13 },
  chip: { background: '#fff', border: '1px solid #dadce0', borderRadius: 999, padding: '5px 12px', fontSize: 13, fontWeight: 600, color: '#3c4043', cursor: 'pointer', whiteSpace: 'nowrap' },
  chipOn: { background: '#1a73e8', border: '1px solid #1a73e8', color: '#fff' },
  btnGhost: { background: '#fff', color: '#202124', border: '1.5px solid #dadce0', borderRadius: 8, padding: '7px 13px', fontSize: 13.5, fontWeight: 700, cursor: 'pointer' },
  btnPrimary: { background: '#1a73e8', color: '#fff', border: '1.5px solid #1a73e8', borderRadius: 8, padding: '7px 13px', fontSize: 13.5, fontWeight: 700, cursor: 'pointer' },
  kpis: { display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(150px, 1fr))', gap: 8, marginBottom: 6 },
  kpi: { background: '#fff', border: '1px solid #dadce0', borderRadius: 12, padding: '11px 14px' },
  kpiValue: { fontSize: 24, fontWeight: 800, lineHeight: 1.15, fontVariantNumeric: 'tabular-nums' },
  kpiLabel: { fontSize: 12.5, color: '#3c4043', fontWeight: 600, marginTop: 2 },
  kpiHint: { fontSize: 11.5, color: '#5f6368', marginTop: 2 },
  section: { marginTop: 18 },
  sectionHead: { fontSize: 15, fontWeight: 800, marginBottom: 2 },
  sectionNote: { fontSize: 12.5, color: '#5f6368', marginBottom: 10, lineHeight: 1.45 },
  sectionTitle: { fontSize: 12, fontWeight: 800, color: '#5f6368', textTransform: 'uppercase', letterSpacing: '0.05em', margin: '14px 0 8px' },
  grid: { display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(300px, 1fr))', gap: 12 },
  wide: { gridColumn: '1 / -1' },
  tableWrap: { background: '#fff', borderRadius: 12, overflow: 'auto', boxShadow: '0 2px 12px rgba(0,0,0,0.08)' },
  table: { width: '100%', borderCollapse: 'collapse', fontSize: 13 },
  th: { textAlign: 'left', padding: '10px 12px', borderBottom: '2px solid #e8eaed', color: '#5f6368', fontWeight: 700, whiteSpace: 'nowrap', background: '#fff' },
  td: { padding: '9px 12px', borderBottom: '1px solid #f1f3f4', verticalAlign: 'top', fontVariantNumeric: 'tabular-nums' },
  trAlt: { background: '#fafbfc' },
  linkBtn: { background: 'none', border: 'none', padding: 0, font: 'inherit', color: '#1a73e8', fontWeight: 700, cursor: 'pointer' },
  pitchRow: { display: 'flex', gap: 8, alignItems: 'center', flexWrap: 'wrap', marginTop: 10, fontSize: 12.5 },
  select: { border: '1px solid #dadce0', borderRadius: 8, padding: '7px 10px', font: 'inherit', fontSize: 13.5, background: '#fff', maxWidth: '100%' },
  dlCard: {
    background: '#fff', borderRadius: 12, padding: '12px 14px', boxShadow: '0 2px 12px rgba(0,0,0,0.08)',
    display: 'flex', gap: 12, flexWrap: 'wrap', alignItems: 'flex-end',
  },
  dlField: { display: 'flex', flexDirection: 'column', gap: 4, minWidth: 0 },
  dlLabel: { fontSize: 11, fontWeight: 800, color: '#5f6368', textTransform: 'uppercase', letterSpacing: '0.05em' },
  dlButtons: { display: 'flex', gap: 8 },
  dlNote: { flexBasis: '100%', fontSize: 12, color: '#5f6368', lineHeight: 1.45 },
  err: { background: '#fce8e6', color: '#c5221f', borderRadius: 8, padding: '8px 11px', fontSize: 13, marginBottom: 12 },
  dim: { color: '#80868b', fontSize: 13 },
};
